import React, { useEffect, useRef, useState } from 'react';
import { CloudUpload, Film, FolderOpen, FileText, Archive, Upload, Link2, X } from 'lucide-react';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v', '.wmv', '.flv'];
const XML_EXTENSIONS = ['.xml'];
const ZIP_EXTENSIONS = ['.zip', '.gz', '.tar', '.7z', '.rar'];
const DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.json'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.svg', '.webp'];

function categorizeFiles(files) {
  const videos = [];
  const xmlFiles = [];
  const zipFiles = [];
  const documents = [];
  const images = [];
  const other = [];

  for (const file of files) {
    const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
    if (VIDEO_EXTENSIONS.includes(ext)) videos.push(file);
    else if (XML_EXTENSIONS.includes(ext)) xmlFiles.push(file);
    else if (ZIP_EXTENSIONS.includes(ext)) zipFiles.push(file);
    else if (DOC_EXTENSIONS.includes(ext)) documents.push(file);
    else if (IMAGE_EXTENSIONS.includes(ext)) images.push(file);
    else other.push(file);
  }

  return { videos, xmlFiles, zipFiles, documents, images, other };
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

async function getFilesFromDataTransfer(dataTransfer) {
  const files = [];

  async function readEntry(entry) {
    if (entry.isFile) {
      const file = await new Promise((resolve) => entry.file(resolve));
      // Accept all files — no extension filtering
      if (file.name && !file.name.startsWith('.')) {
        files.push(file);
      }
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      let allEntries = [];
      // readEntries may return partial results; keep calling until empty
      let batch;
      do {
        batch = await new Promise((resolve) => reader.readEntries(resolve));
        allEntries = allEntries.concat(Array.from(batch));
      } while (batch.length > 0);
      for (const child of allEntries) {
        await readEntry(child);
      }
    }
  }

  const items = Array.from(dataTransfer.items || []);
  for (const item of items) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      await readEntry(entry);
    }
  }

  return files;
}

export default function DropZoneUploader({
  onFilesSelected,
  onFileSelected,
  onGcsUri,
  onWeTransferUrl,
  isUploading,
  uploadProgress,
}) {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragState, setDragState] = useState('idle');
  const [gcsUri, setGcsUri] = useState('');
  const [weTransferUrl, setWeTransferUrl] = useState('');
  const [error, setError] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('files');

  useEffect(() => {
    console.log('[DropZone:Uploader] state update', {
      dragState,
      isUploading,
      uploadProgress,
      uploadMode,
      stagedCount: stagedFiles.length,
      hasUri: Boolean(gcsUri),
    });
  }, [dragState, isUploading, uploadProgress, gcsUri, stagedFiles, uploadMode]);

  useEffect(() => {
    if (isUploading) {
      setDragState('uploading');
      return;
    }
    setDragState((s) => (s === 'uploading' ? 'idle' : s));
  }, [isUploading]);

  function handleFiles(fileList) {
    // Accept all files — no extension filtering. Skip hidden/dot files only.
    const allFiles = Array.from(fileList).filter((f) => f.name && !f.name.startsWith('.'));
    if (allFiles.length === 0) {
      setError('No files found in the selection.');
      return;
    }

    const categories = categorizeFiles(allFiles);
    console.log('[DropZone:Uploader] handleFiles', {
      total: allFiles.length,
      videos: categories.videos.length,
      xml: categories.xmlFiles.length,
      zip: categories.zipFiles.length,
      documents: categories.documents.length,
      images: categories.images.length,
      other: categories.other.length,
    });

    setError('');
    setStagedFiles(allFiles);

    if (onFilesSelected) {
      onFilesSelected(allFiles);
    } else if (onFileSelected && categories.videos.length === 1 && categories.xmlFiles.length === 0) {
      onFileSelected(categories.videos[0]);
    }
  }

  function handleBrowseFiles() {
    if (isUploading) return;
    console.log('[DropZone:Uploader] browse files click');
    fileInputRef.current?.click();
  }

  function handleBrowseFolder() {
    if (isUploading) return;
    console.log('[DropZone:Uploader] browse folder click');
    folderInputRef.current?.click();
  }

  async function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragState(isUploading ? 'uploading' : 'idle');

    const hasItems = event.dataTransfer.items?.length > 0;
    const firstItem = event.dataTransfer.items?.[0];
    const isFolder = firstItem?.webkitGetAsEntry?.()?.isDirectory;

    console.log('[DropZone:Uploader] drop', { hasItems, isFolder });

    if (isFolder || (hasItems && event.dataTransfer.items.length > 1)) {
      const files = await getFilesFromDataTransfer(event.dataTransfer);
      if (files.length > 0) {
        handleFiles(files);
        return;
      }
    }

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length > 0) {
      handleFiles(files);
    }
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploading) setDragState('dragOver');
  }

  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploading) setDragState('idle');
  }

  function handleUriChange(nextValue) {
    console.log('[DropZone:Uploader] gcsUri change', { value: nextValue });
    setGcsUri(nextValue);
    setError('');
    onGcsUri(nextValue.trim());
  }

  function handleWeTransferSubmit() {
    const url = weTransferUrl.trim();
    if (!url) return;

    if (!/wetransfer\.com/i.test(url)) {
      setError('Please enter a valid WeTransfer URL.');
      return;
    }

    console.log('[DropZone:Uploader] WeTransfer URL submitted', { url });
    setError('');
    if (onWeTransferUrl) {
      onWeTransferUrl(url);
    }
  }

  function removeFile(index) {
    setStagedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (onFilesSelected) onFilesSelected(next);
      return next;
    });
  }

  const categories = categorizeFiles(stagedFiles);

  return (
    <div className="rounded-3xl border border-[#1A1A1A] bg-[#0A0A0A] p-5 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => handleFiles(event.target.files)}
      />
      <input
        ref={folderInputRef}
        type="file"
        // eslint-disable-next-line react/no-unknown-property
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={(event) => handleFiles(event.target.files)}
      />

      {/* Upload mode tabs */}
      <div className="mb-4 flex gap-2">
        {[
          { id: 'files', label: 'Files', icon: <Film size={14} /> },
          { id: 'folder', label: 'Folder', icon: <FolderOpen size={14} /> },
          { id: 'wetransfer', label: 'WeTransfer', icon: <Link2 size={14} /> },
        ].map((mode) => (
          <button
            key={mode.id}
            type="button"
            onClick={() => { setUploadMode(mode.id); setError(''); }}
            className={[
              'inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all duration-200',
              uploadMode === mode.id
                ? 'border border-[#E8B800] bg-[#1A1700] text-[#E8B800]'
                : 'border border-[#1A1A1A] bg-[#111111] text-zinc-400 hover:border-zinc-500 hover:text-zinc-300',
            ].join(' ')}
          >
            {mode.icon}
            {mode.label}
          </button>
        ))}
      </div>

      {/* Drop zone area (files + folder modes) */}
      {(uploadMode === 'files' || uploadMode === 'folder') && (
        <button
          type="button"
          onClick={uploadMode === 'folder' ? handleBrowseFolder : handleBrowseFiles}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={[
            'group flex w-full flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed px-6 py-10 text-center transition-all duration-200',
            dragState === 'dragOver'
              ? 'border-[#E8B800] bg-[#111111] shadow-[0_0_32px_rgba(232,184,0,0.22)]'
              : 'border-gray-600 bg-[#050505] hover:border-[#E8B800] hover:bg-[#0F0F0F]',
            isUploading ? 'cursor-wait' : 'cursor-pointer',
          ].join(' ')}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#1A1A1A] bg-[#111111] text-[#E8B800]">
            {isUploading ? (
              <CloudUpload size={28} className="animate-pulse" />
            ) : uploadMode === 'folder' ? (
              <FolderOpen size={28} />
            ) : (
              <Upload size={28} />
            )}
          </div>

          <div className="space-y-2">
            <div className="text-lg font-semibold text-white">
              {isUploading
                ? 'Uploading files to Drop Zone'
                : uploadMode === 'folder'
                  ? 'Select a game folder'
                  : 'Drag and drop game files'}
            </div>
            <div className="text-sm text-zinc-400">
              {uploadMode === 'folder'
                ? 'Select a folder containing paired video + XML files for each game.'
                : 'Drop video files, XML metadata, or ZIP archives. You can also drop a folder.'}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1">
              <Film size={14} />
              Video
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1">
              <FileText size={14} />
              XML / PDF / Docs
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1">
              <Archive size={14} />
              ZIP / Archives
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1">
              <FolderOpen size={14} />
              Folders
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A] bg-[#111111] px-3 py-1">
              <CloudUpload size={14} />
              Max 5GB per file
            </span>
          </div>
        </button>
      )}

      {/* WeTransfer URL input */}
      {uploadMode === 'wetransfer' && (
        <div className="rounded-3xl border-2 border-dashed border-gray-600 bg-[#050505] p-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#1A1A1A] bg-[#111111] text-[#E8B800]">
              <Link2 size={28} />
            </div>
            <div className="space-y-2 text-center">
              <div className="text-lg font-semibold text-white">Paste a WeTransfer link</div>
              <div className="text-sm text-zinc-400">
                Share a WeTransfer URL and we&apos;ll download, extract, and analyze the contents.
              </div>
            </div>
            <div className="flex w-full max-w-lg gap-2">
              <input
                type="text"
                value={weTransferUrl}
                onChange={(e) => setWeTransferUrl(e.target.value)}
                placeholder="https://wetransfer.com/downloads/..."
                className="flex-1 rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E8B800]"
              />
              <button
                type="button"
                onClick={handleWeTransferSubmit}
                disabled={!weTransferUrl.trim() || isUploading}
                className={[
                  'rounded-2xl px-5 py-3 text-sm font-semibold transition-all duration-200',
                  weTransferUrl.trim() && !isUploading
                    ? 'bg-[#E8B800] text-black hover:bg-[#f5c400]'
                    : 'cursor-not-allowed border border-[#1A1A1A] bg-[#111111] text-zinc-500',
                ].join(' ')}
              >
                Fetch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staged files preview */}
      {stagedFiles.length > 0 && (
        <div className="mt-4 rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              {stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} staged
              {categories.videos.length > 0 && (
                <span className="ml-2 text-[#E8B800]">
                  {categories.videos.length} video{categories.videos.length !== 1 ? 's' : ''}
                </span>
              )}
              {categories.xmlFiles.length > 0 && (
                <span className="ml-2 text-blue-400">
                  {categories.xmlFiles.length} XML
                </span>
              )}
              {categories.zipFiles.length > 0 && (
                <span className="ml-2 text-purple-400">
                  {categories.zipFiles.length} ZIP
                </span>
              )}
              {categories.documents.length > 0 && (
                <span className="ml-2 text-green-400">
                  {categories.documents.length} doc{categories.documents.length !== 1 ? 's' : ''}
                </span>
              )}
              {categories.images.length > 0 && (
                <span className="ml-2 text-cyan-400">
                  {categories.images.length} image{categories.images.length !== 1 ? 's' : ''}
                </span>
              )}
              {categories.other.length > 0 && (
                <span className="ml-2 text-zinc-400">
                  {categories.other.length} other
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setStagedFiles([]); if (onFilesSelected) onFilesSelected([]); }}
              className="text-xs text-zinc-500 hover:text-zinc-300"
            >
              Clear all
            </button>
          </div>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {stagedFiles.map((file, index) => {
              const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
              const isVideo = VIDEO_EXTENSIONS.includes(ext);
              const isXml = XML_EXTENSIONS.includes(ext);
              const isZip = ZIP_EXTENSIONS.includes(ext);
              const isImage = IMAGE_EXTENSIONS.includes(ext);
              return (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm hover:bg-[#1A1A1A]"
                >
                  {isVideo ? (
                    <Film size={14} className="shrink-0 text-[#E8B800]" />
                  ) : isXml ? (
                    <FileText size={14} className="shrink-0 text-blue-400" />
                  ) : isZip ? (
                    <Archive size={14} className="shrink-0 text-purple-400" />
                  ) : isImage ? (
                    <Film size={14} className="shrink-0 text-cyan-400" />
                  ) : (
                    <FileText size={14} className="shrink-0 text-green-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-zinc-300">{file.name}</span>
                  <span className="shrink-0 text-xs text-zinc-600">{formatSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="shrink-0 text-zinc-600 hover:text-zinc-300"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="my-5 flex items-center gap-4">
        <div className="h-px flex-1 bg-[#1A1A1A]" />
        <span className="text-xs uppercase tracking-[0.24em] text-zinc-600">or paste a GCS URI</span>
        <div className="h-px flex-1 bg-[#1A1A1A]" />
      </div>

      <div className="space-y-2">
        <label htmlFor="drop-zone-gcs" className="text-xs font-medium uppercase tracking-[0.22em] text-zinc-500">
          Cloud Storage URI
        </label>
        <input
          id="drop-zone-gcs"
          type="text"
          value={gcsUri}
          onChange={(event) => handleUriChange(event.target.value)}
          onPaste={(event) => {
            console.log('[DropZone:Uploader] gcsUri paste', {
              value: event.clipboardData.getData('text'),
            });
          }}
          placeholder="gs://slam-video-analysis/uploads/game-film.mp4"
          className="w-full rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E8B800]"
        />
      </div>

      {isUploading ? (
        <div className="mt-5 space-y-2">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-zinc-500">
            <span>Upload Progress</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[#111111]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#E8B800] to-amber-400 transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      ) : null}

      {error ? <div className="mt-4 text-sm text-red-400">{error}</div> : null}
    </div>
  );
}
